import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { IconType } from 'react-icons';
import { FiArrowUpRight } from 'react-icons/fi';

export type EcosystemPlaybookGlow = 'green';

const GLOW_COLOR = 'rgba(72, 187, 120, 0.12)';

type EcosystemPlaybookCardProps = {
  title: string;
  description: string;
  count: number | null | undefined;
  url?: string | null;
  icon: IconType;
  glow?: EcosystemPlaybookGlow;
  ctaLabel?: string;
  onView: () => void;
};

export function EcosystemPlaybookCard({
  title,
  description,
  count,
  url,
  icon,
  ctaLabel = 'View cocoOpps',
  onView,
}: EcosystemPlaybookCardProps) {
  const hasCount = typeof count === 'number';
  const displayCount = hasCount ? count.toLocaleString() : '—';

  return (
    <Box
      position="relative"
      overflow="hidden"
      bg="white"
      borderRadius="2xl"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      p={{ base: 6, md: 8 }}
      h="100%"
      display="flex"
      flexDirection="column"
      transition="border-color 0.2s, box-shadow 0.2s"
      _hover={{
        borderColor: 'gray.300',
        boxShadow: 'md',
      }}
    >
      <Box
        position="absolute"
        top="-40px"
        right="-40px"
        w="200px"
        h="200px"
        borderRadius="full"
        bg={GLOW_COLOR}
        filter="blur(40px)"
        pointerEvents="none"
      />

      <Flex
        position="relative"
        justify="space-between"
        align="flex-start"
        gap={4}
        mb={4}
      >
        <HStack spacing={3} align="center" minW={0}>
          <Flex
            align="center"
            justify="center"
            w="40px"
            h="40px"
            flexShrink={0}
            borderRadius="full"
            bg="gray.100"
          >
            <Icon as={icon} boxSize={5} color="gray.700" />
          </Flex>
          <Text
            fontWeight="700"
            fontSize={{ base: 'lg', md: 'xl' }}
            color="gray.800"
            lineHeight="1.2"
          >
            {title}
          </Text>
        </HStack>

        <VStack spacing={0} align="flex-end" flexShrink={0}>
          <Text
            fontWeight="800"
            fontSize={{ base: '3xl', md: '4xl' }}
            lineHeight="1"
            color="gray.900"
          >
            {displayCount}
          </Text>
          <Text
            fontSize="xs"
            fontWeight="600"
            letterSpacing="0.08em"
            color="gray.500"
            textTransform="uppercase"
          >
            Overlaps
          </Text>
        </VStack>
      </Flex>

      <Text
        position="relative"
        color="gray.500"
        fontSize="md"
        lineHeight="1.55"
        pr={{ md: 8 }}
      >
        {description}
      </Text>

      <Flex
        position="relative"
        justify="space-between"
        align="center"
        gap={3}
        wrap="wrap"
        mt="auto"
        pt={6}
      >
        <HStack
          spacing={2}
          bg="gray.100"
          borderRadius="full"
          px={3}
          py={1.5}
        >
          <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
          <Text fontSize="sm" fontWeight="500" color="gray.700">
            {hasCount
              ? `${count.toLocaleString()} matched accounts`
              : '— matched accounts'}
          </Text>
        </HStack>

        <Button
          bg="brand.300"
          color="gray.800"
          borderRadius="lg"
          fontWeight="600"
          h="40px"
          px={4}
          rightIcon={<Icon as={FiArrowUpRight} boxSize={4} />}
          isDisabled={!url}
          _hover={{ bg: 'brand.200' }}
          onClick={onView}
        >
          {ctaLabel}
        </Button>
      </Flex>
    </Box>
  );
}
